import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { certificationService } from '@/lib/api/services/certification.service';

export function useCertificationCohorts(statusFilter?: string) {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const cohortsKey = ['certification-cohorts', statusFilter];
  const myCohortsKey = ['certification-cohorts-mine'];

  const {
    data: cohorts = [],
    isLoading: isLoadingCohorts,
  } = useQuery({
    queryKey: cohortsKey,
    queryFn: () => certificationService.listCohorts(statusFilter),
    enabled: !!user?.id && statusFilter !== '__disabled__',
    staleTime: 2 * 60 * 1000,
  });

  const {
    data: myCohorts = [],
    isLoading: isLoadingMyCohorts,
  } = useQuery({
    queryKey: myCohortsKey,
    queryFn: () => certificationService.getMyCohorts(),
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['certification-cohorts'] });
    queryClient.invalidateQueries({ queryKey: myCohortsKey });
  };

  const createCohortMutation = useMutation({
    mutationFn: (data: { name: string; description?: string | null; max_members?: number }) =>
      certificationService.createCohort(data),
    onSuccess: () => {
      invalidateAll();
      toast.success('Cohort created! You\'re the organizer.');
    },
    onError: () => {
      toast.error('Failed to create cohort');
    },
  });

  const joinCohortMutation = useMutation({
    mutationFn: (cohortId: string) => certificationService.joinCohort(cohortId),
    onSuccess: () => {
      invalidateAll();
      toast.success('You\'ve joined the cohort!');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to join cohort');
    },
  });

  const leaveCohortMutation = useMutation({
    mutationFn: (cohortId: string) => certificationService.leaveCohort(cohortId),
    onSuccess: () => {
      invalidateAll();
      toast.success('You\'ve left the cohort');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to leave cohort');
    },
  });

  return {
    cohorts,
    myCohorts,
    isLoading: isLoadingCohorts || isLoadingMyCohorts,
    createCohort: createCohortMutation.mutateAsync,
    joinCohort: joinCohortMutation.mutateAsync,
    leaveCohort: leaveCohortMutation.mutateAsync,
    isCreating: createCohortMutation.isPending,
    isJoining: joinCohortMutation.isPending,
    isLeaving: leaveCohortMutation.isPending,
  };
}

export function useCohortDetail(cohortId: string) {
  const { user } = useUser();

  const {
    data: cohort,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['certification-cohort', cohortId],
    queryFn: () => certificationService.getCohort(cohortId),
    enabled: !!user?.id && !!cohortId,
  });

  return {
    cohort,
    members: cohort?.members || [],
    isLoading,
    error,
  };
}
